"use client"

import { useEffect, useState } from "react"
import {
  Plus, Edit, UserPlus, CheckCircle, Clock, AlertCircle,
  ChevronLeft, ChevronRight, Loader2
} from "lucide-react"
import { get, put } from "../utility/fetch"
import toast from "react-hot-toast"

export default function EngagementManagement() {
  const [searchTerm, setSearchTerm] = useState("")
  const [filterStatus, setFilterStatus] = useState("all")
  const [engagements, setEngagements] = useState([])
  const [page, setPage] = useState(1)
  const [limit] = useState(5)
  const [totalPages, setTotalPages] = useState(1)

  const [isLoadingPage, setIsLoadingPage] = useState(false)
  const [loadingAssignId, setLoadingAssignId] = useState(null)
  const [loadingProgressId, setLoadingProgressId] = useState(null)
  const [loadingResolveId, setLoadingResolveId] = useState(null)

  const [agencyUsers, setAgencyUsers] = useState([])
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [selectedEngagementId, setSelectedEngagementId] = useState(null)
  const [isLoadingAgents, setIsLoadingAgents] = useState(false)



  const fetchEngagements = async () => {
    try {
      setIsLoadingPage(true)
      const res = await get(`/api/customerengagement/list/${page}/${limit}`)
      if (res?.data?.recordList) {
        setEngagements(res.data.recordList)
        setTotalPages(res.data.metadata?.totalPages || 1)
      }
    } catch (error) {
      console.error("Error fetching engagements:", error)
    } finally {
      setIsLoadingPage(false)
    }
  }

  useEffect(() => {
    fetchEngagements()
  }, [searchTerm, filterStatus, page])

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return <Clock className="w-4 h-4" />
      case "in-progress":
        return <AlertCircle className="w-4 h-4" />
      case "resolved":
        return <CheckCircle className="w-4 h-4" />
      default:
        return <Clock className="w-4 h-4" />
    }
  }

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "bg-yellow-100 text-yellow-800"
      case "in-progress":
        return "bg-blue-100 text-blue-800"
      case "resolved":
        return "bg-green-100 text-green-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const handleAssign = async (engagementId) => {
    setSelectedEngagementId(engagementId)
    setShowAssignModal(true)
    setIsLoadingAgents(true)

    try {
      const res = await get("/api/auth/list/agency-user/1/1000")
      if (res?.data?.recordList) {
        setAgencyUsers(res.data.recordList)
      } else {
        toast.error("Failed to fetch agency users")
      }
    } catch (err) {
      console.error("Fetch error:", err)
      toast.error("Error fetching agency users")
    } finally {
      setIsLoadingAgents(false)
    }
  }


  const handleStatusUpdate = async (engagementId) => {
    setLoadingProgressId(engagementId)
    try {
      await put(`/api/customerengagement/${engagementId}/change_resolution_status`, { status: "in-progress" })
      toast.success(`Status updated to in-progress`)
      fetchEngagements()
    } catch (err) {
      toast.error("Failed to update status")
    } finally {
      setLoadingProgressId(null)
    }
  }

  const handleResolve = async (engagementId) => {
    setLoadingResolveId(engagementId)
    try {
      await put(`/api/customerengagement/${engagementId}/change_resolution_status`, { status: "resolved" })
      toast.success(`Engagement resolved`)
      fetchEngagements()
    } catch (err) {
      toast.error("Failed to resolve engagement")
    } finally {
      setLoadingResolveId(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Engagement Management</h1>
        {/* <button className="bg-primary text-white px-4 py-2 rounded-lg flex items-center gap-2">
          <Plus className="w-4 h-4" /> Create Engagement
        </button> */}
      </div>

      <div className="flex gap-4">
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => { setPage(1); setSearchTerm(e.target.value) }}
          className="border px-3 py-2 rounded-md w-full"
        />
        <select
          value={filterStatus}
          onChange={(e) => { setPage(1); setFilterStatus(e.target.value) }}
          className="border px-3 py-2 rounded-md"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="in-progress">In Progress</option>
          <option value="resolved">Resolved</option>
        </select>
      </div>

      {isLoadingPage ? (
        <div className="flex justify-center items-center py-10">
          <Loader2 className="w-6 h-6 animate-spin text-gray-600" />
        </div>
      ) : (
        <div className="space-y-4">
          {engagements.map((engagement) => {
            const isAssigned = engagement.agencyUserId > 0
            const status = engagement.status.toLowerCase()
            const isInProgress = status === "in-progress"
            const isResolved = status === "resolved"
            const isBusy = loadingAssignId === engagement.trackingId ||
              loadingProgressId === engagement.trackingId ||
              loadingResolveId === engagement.trackingId

            return (
              <div key={engagement.id} className="border p-4 rounded-xl shadow-sm bg-white">
                <div className="flex justify-between">
                  <div>
                    <h2 className="font-bold text-lg">{engagement.type}</h2>
                    <p className="text-sm text-gray-600">{engagement.trackingId}</p>
                    <p className="mt-2 text-gray-700">{engagement.message}</p>
                  </div>
                  <div className="flex gap-2 items-start">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(engagement.status)}`}>
                      {getStatusIcon(engagement.status)}
                      {engagement.status}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4 text-sm text-gray-700">
                  <div>
                    <p className="text-gray-500">Location</p>
                    <p>{engagement.location}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Email</p>
                    <p>{engagement.contactEmail}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Assigned To</p>
                    <p>{isAssigned ? `User ${engagement.agencyUserId}` : "Unassigned"}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Submitted</p>
                    <p>{formatDate(engagement.createdAt)}</p>
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleAssign(engagement.trackingId)}
                    className="border px-3 py-1 rounded flex items-center gap-1 disabled:opacity-50"
                    disabled={isAssigned || loadingAssignId === engagement.trackingId}
                  >
                    {loadingAssignId === engagement.trackingId ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <UserPlus className="w-4 h-4" />
                    )}
                    Assign
                  </button>

                  <button
                    onClick={() => handleStatusUpdate(engagement.trackingId)}
                    className="border px-3 py-1 rounded flex items-center gap-1 disabled:opacity-50"
                    disabled={isInProgress || isResolved || loadingProgressId === engagement.trackingId}
                  >
                    {loadingProgressId === engagement.trackingId ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Edit className="w-4 h-4" />
                    )}
                    In Progress
                  </button>

                  <button
                    onClick={() => handleResolve(engagement.trackingId)}
                    className="border px-3 py-1 rounded flex items-center gap-1 disabled:opacity-50"
                    disabled={isResolved || loadingResolveId === engagement.trackingId}
                  >
                    {loadingResolveId === engagement.trackingId ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    Resolve
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div className="flex justify-center items-center gap-4 mt-6">
        <button disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="border px-3 py-1 rounded disabled:opacity-50">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span>Page {page} of {totalPages}</span>
        <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="border px-3 py-1 rounded disabled:opacity-50">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {showAssignModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">Assign Agent</h2>
            {isLoadingAgents ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-gray-600" />
                <span className="ml-2 text-gray-600">Loading agents...</span>
              </div>
            ) : (
              <select
                className="w-full border px-3 py-2 rounded mb-4"
                onChange={async (e) => {
                  const agencyUserId = parseInt(e.target.value)
                  if (!agencyUserId) return

                  try {
                    setLoadingAssignId(selectedEngagementId)
                    await put(`/api/customerengagement/${selectedEngagementId}/assign-agency-user`, {
                      agencyUserId,
                    })
                    toast.success("User assigned successfully")
                    fetchEngagements()
                  } catch (err) {
                    toast.error("Failed to assign user")
                  } finally {
                    setLoadingAssignId(null)
                    setShowAssignModal(false)
                    setSelectedEngagementId(null)
                  }
                }}
              >
                <option value="">Select an agent</option>
                {agencyUsers.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.username} ({agent.email})
                  </option>
                ))}
              </select>
            )}


            <button
              onClick={() => setShowAssignModal(false)}
              className="text-sm text-white bg-red-700 rounded-sm px-4 py-2 ml-2 disabled:opacity-50 cursor-pointer"
              disabled={isLoadingAgents}
            >
              Cancel
            </button>

          </div>
        </div>
      )}

    </div>
  )
}
